// Performance tab: version × arm comparison (the A/B view, holdout included)
// with 95% ranges, the stage funnel, units sorted weakest first, and a
// drill-down from a unit to its moves (match, classifier, adherence), each
// move's conversation timeline, and its transcript (CallDrawer).

import { Fragment, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { errorText, getCallTimeline, getUnitCalls } from "../../services/talkTracks";
import type {
  Arm,
  BalancedRates,
  ConversationTimeline,
  RateKey,
  StageFunnel,
  StatsFilters,
  TalkTrackEvent,
  TalkTrackTopic,
  TopicStats,
  UnitCall,
  UnitStats,
  VersionArmStats,
} from "../../types/talkTrack";
import type { InputMode } from "../../services/analytics";
import { CallDrawer } from "../analytics/CallDrawer";
import { RangePicker } from "../analytics/bits";
import { formatDateTime, type RangePreset } from "../analytics/format";
import { LOW_SAMPLE, LOW_SAMPLE_USERS, isWeakUnit, pct, rangesApart, skewNote } from "./format";
import { Rate } from "./Rate";

type Better = "higher" | "lower";

const ARM_ORDER: Record<string, number> = { control: 0, candidate: 1, holdout: 2 };
const INPUT_MODES: { key: InputMode | null; label: string }[] = [
  { key: null, label: "All" },
  { key: "voice", label: "Voice" },
  { key: "text", label: "Typed" },
];

// An arrow only when both rows, and the users behind this rate on each
// side, are enough and their 95% ranges don't overlap: small samples
// otherwise read as signal (and a range estimated from a handful of users is
// itself too rough to trust).
function Delta({ row, base, rate, better, minUsers }: {
  row: BalancedRates;
  base: BalancedRates;
  rate: RateKey;
  better: Better;
  minUsers: number;
}) {
  const current = row[rate];
  const baseline = base[rate];
  if (current === null || baseline === null || row.lowSample || base.lowSample) return null;
  if (!rangesApart(row.ci?.[rate], base.ci?.[rate], minUsers)) return null;
  const diff = current - baseline;
  const good = better === "higher" ? diff > 0 : diff < 0;
  return (
    <span className={good ? "delta-up" : "delta-down"}>
      {" "}{diff > 0 ? "▲" : "▼"}{Math.abs(Math.round(diff * 100))}
    </span>
  );
}

// Weak (and trustworthy) first, then well-sampled units, then low-sample
// ones; within a group, lowest answer rate then highest drop-off first.
function compareWeakness(a: UnitStats, b: UnitStats): number {
  const group = (u: UnitStats) => (isWeakUnit(u) ? 0 : u.moves >= LOW_SAMPLE ? 1 : 2);
  if (group(a) !== group(b)) return group(a) - group(b);
  const ra = a.answerRate ?? 2;
  const rb = b.answerRate ?? 2;
  if (ra !== rb) return ra - rb;
  return (b.dropOffRate ?? 0) - (a.dropOffRate ?? 0);
}

function followedText(m: TalkTrackEvent): string {
  if (m.followedSuggestion === null) return "—";
  return m.followedSuggestion ? "yes" : "no";
}

function classifiedText(m: TalkTrackEvent): string {
  if (!m.classifiedBy) return "—";
  return m.directorMs !== null ? `${m.classifiedBy} · ${(m.directorMs / 1000).toFixed(1)}s` : m.classifiedBy;
}

function flagsText(m: TalkTrackEvent): string {
  const flags = [];
  if (m.catchUp) flags.push("catch-up");
  if (m.isRepeat) flags.push("repeat");
  if (m.safetySignal && m.safetySignal !== "none") flags.push(m.safetySignal);
  if (m.factsFilled) flags.push(`${m.factsFilled} fact${m.factsFilled === 1 ? "" : "s"}`);
  return flags.join(" · ") || "—";
}

function Funnel({ funnel }: { funnel: StageFunnel }) {
  const arms = (Object.keys(funnel.arms) as Arm[]).sort((a, b) => (ARM_ORDER[a] ?? 9) - (ARM_ORDER[b] ?? 9));
  return (
    <>
      <div className="muted tt-small tt-drill-label">
        {funnel.conversations} conversation{funnel.conversations === 1 ? "" : "s"} from {funnel.users} user
        {funnel.users === 1 ? "" : "s"}. A conversation counts for every stage up to the furthest one it logged a move
        in; each user weighs the same.
      </div>
      <div className="rubric-bars">
        {funnel.stages.map((s) => (
          <div className="tt-funnel-row" key={s.stage}>
            <span className="rubric-bar-label">{s.stage}</span>
            <span className="rubric-bar-track">
              <span className="rubric-bar-fill" style={{ width: `${Math.round((s.reached ?? 0) * 100)}%` }} />
            </span>
            <span className="rubric-bar-value">{pct(s.reached)}</span>
            <span className="muted tt-small">
              {s.conversations} conv.
              {arms.length > 1 &&
                arms.map((arm) => {
                  const reached = funnel.arms[arm]?.stages.find((x) => x.stage === s.stage)?.reached;
                  return ` · ${arm} ${pct(reached)}`;
                })}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

interface Props {
  topic: TalkTrackTopic;
  stats: TopicStats | null;
  loading: boolean;
  error: string | null;
  preset: RangePreset;
  onPreset: (preset: RangePreset) => void;
  filters: StatsFilters;
  onFilters: (filters: StatsFilters) => void;
  // Unit ids present in the selected version (others can't be jumped to).
  unitIds: Set<string>;
  onFocusUnit: (unitId: string) => void;
}

export function PerformanceTab({
  topic, stats, loading, error, preset, onPreset, filters, onFilters, unitIds, onFocusUnit,
}: Props) {
  const [unit, setUnit] = useState<UnitStats | null>(null);
  const [calls, setCalls] = useState<UnitCall[] | null>(null);
  const [callsError, setCallsError] = useState<string | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<ConversationTimeline | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [drawerCallId, setDrawerCallId] = useState<string | null>(null);
  // Latest click wins: a slow response for an earlier unit/call is dropped.
  const latestUnit = useRef<string | null>(null);
  const latestCall = useRef<string | null>(null);

  const days = stats?.days ?? 30;
  const minUsers = stats?.lowSampleUsers ?? LOW_SAMPLE_USERS;

  async function selectUnit(u: UnitStats) {
    latestUnit.current = u.unitId;
    setUnit(u);
    setCalls(null);
    setCallsError(null);
    setCallId(null);
    setTimeline(null);
    try {
      const res = await getUnitCalls(topic.id, u.unitId, days, filters);
      if (latestUnit.current === u.unitId) setCalls(res.calls);
    } catch (e) {
      if (latestUnit.current === u.unitId) setCallsError(errorText(e));
    }
  }

  async function showTimeline(id: string) {
    latestCall.current = id;
    setCallId(id);
    setTimeline(null);
    setTimelineError(null);
    try {
      const res = await getCallTimeline(id);
      if (latestCall.current === id) setTimeline(res);
    } catch (e) {
      if (latestCall.current === id) setTimelineError(errorText(e));
    }
  }

  const versions = [...(stats?.versions ?? [])].sort(
    (a, b) => (b.version ?? 0) - (a.version ?? 0) || (ARM_ORDER[a.arm] ?? 9) - (ARM_ORDER[b.arm] ?? 9),
  );
  // Candidate rows compare with live's control arm; live's control arm with
  // its holdout (the same users' version without a talk track).
  const liveRow = versions.find((v) => v.version === topic.liveVersion && v.arm === "control") ?? null;
  const holdoutRow = versions.find((v) => v.version === topic.liveVersion && v.arm === "holdout") ?? null;
  const baseFor = (v: VersionArmStats): VersionArmStats | null =>
    v.arm === "candidate" ? liveRow : v === liveRow ? holdoutRow : null;
  const units = [...(stats?.units ?? [])].sort(compareWeakness);
  const isUnitMove = (m: TalkTrackEvent) =>
    !!unit && (m.unitMatch === "related" ? m.trackId === unit.unitId : m.unitId === unit.unitId);

  const armLabel = (v: { arm: Arm; version: number | null }) =>
    v.arm === "holdout"
      ? "holdout (no talk track)"
      : v.arm === "candidate"
        ? "candidate"
        : v.version === topic.liveVersion
          ? "live"
          : "control";

  const rateCell = (v: VersionArmStats, rate: RateKey, better: Better) => {
    const base = baseFor(v);
    return (
      <td>
        <Rate value={v[rate]} range={v.ci?.[rate]} />
        {base && <Delta row={v} base={base} rate={rate} better={better} minUsers={minUsers} />}
      </td>
    );
  };

  // Connection boundaries in the timeline (a conversation spans reconnects).
  const callIndex = new Map((timeline?.calls ?? []).map((c, i) => [c.callId, i]));

  return (
    <div className="tt-perf">
      <div className="tt-toolbar">
        <div className="muted tt-small">
          App traffic only. Live v{topic.liveVersion ?? "—"}
          {topic.candidateVersion !== null && ` vs candidate v${topic.candidateVersion} at ${topic.rolloutPct}%`}.
          {" "}Version rates weigh each user equally, with a 95% range over users; reconnects count as one conversation.
        </div>
        <div className="spacer" />
        <div className="seg" role="group" aria-label="Input mode">
          {INPUT_MODES.map((m) => (
            <button key={m.label} className={filters.inputMode === m.key ? "active" : ""}
              title={m.key ? `Moves made on ${m.key === "text" ? "typed" : "voice"} connections only` : undefined}
              onClick={() => onFilters({ ...filters, inputMode: m.key })}>
              {m.label}
            </button>
          ))}
        </div>
        <label className="tt-toggle" title="Drop the user with the most moves on this topic">
          <input type="checkbox" checked={filters.excludeTopUser}
            onChange={(e) => onFilters({ ...filters, excludeTopUser: e.target.checked })} />
          Exclude top user
        </label>
        <RangePicker value={preset} onChange={onPreset} />
      </div>
      {stats?.excludedTopUser && (
        <div className="muted tt-small">
          Leaving out the most active user: {stats.excludedTopUser.moves} moves
          ({pct(stats.excludedTopUser.share)} of this topic's traffic).
        </div>
      )}

      {loading ? (
        <div className="state-box">Loading performance…</div>
      ) : error ? (
        <div className="state-box error">{error}</div>
      ) : !stats || (stats.versions.length === 0 && stats.units.length === 0) ? (
        <div className="state-box">No conversations used this topic in the last {days} days.</div>
      ) : (
        <>
          <div className="panel-head">Versions × arms</div>
          <div className="version-compare-wrap">
            <table className="version-compare">
              <thead>
                <tr>
                  <th>version</th>
                  <th>arm</th>
                  <th title="Reconnects count as one conversation">conversations</th>
                  <th>users</th>
                  <th>answer rate</th>
                  <th>drop-off</th>
                  <th>deflect</th>
                  <th>offer acceptance</th>
                  <th>eval score</th>
                  <th>user turns</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={`${v.version}-${v.arm}`}>
                    <td>{v.version !== null ? `v${v.version}` : "—"}</td>
                    <td>{armLabel(v)}</td>
                    <td title={`${v.calls} connections`}>{v.conversations}</td>
                    <td>
                      {v.users}
                      {v.lowSample && (
                        <span className="badge low-sample"
                          title={`Fewer than ${minUsers} users: the ranges are wide, don't compare yet`}>
                          low n
                        </span>
                      )}
                      {skewNote(v.topUserShare) && (
                        <span className="badge skew" title="Rates weigh each user equally, but most traffic is one person">
                          {skewNote(v.topUserShare)}
                        </span>
                      )}
                    </td>
                    {rateCell(v, "answerRate", "higher")}
                    {rateCell(v, "dropOffRate", "lower")}
                    {rateCell(v, "deflectRate", "lower")}
                    {rateCell(v, "offerAcceptance", "higher")}
                    {/* No range for the call-level eval score, so no arrow either. */}
                    <td>{pct(v.evalScore)}</td>
                    <td>{v.userTurns !== null ? v.userTurns.toFixed(1) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="muted tt-small">
            {liveRow && versions.some((v) => v.arm === "candidate") && `Candidate deltas are against live v${topic.liveVersion}. `}
            {liveRow && holdoutRow && "Live's deltas are against its holdout. "}
            Rows from fewer than {minUsers} users are marked low n; arrows show only between rows with enough users
            whose 95% ranges don't overlap.
          </div>

          {stats.funnel && stats.funnel.conversations > 0 && (
            <>
              <div className="panel-head">Stage funnel</div>
              <Funnel funnel={stats.funnel} />
            </>
          )}

          <div className="panel-head">Units · weakest first</div>
          {units.length === 0 ? (
            <div className="state-box">No unit-level moves recorded yet.</div>
          ) : (
            <div className="table-scroll">
              <table className="guide-table">
                <thead>
                  <tr>
                    <th className="no-sort">unit</th>
                    <th className="no-sort">moves</th>
                    <th className="no-sort">answered</th>
                    <th className="no-sort">drop-off</th>
                    <th className="no-sort">deflect</th>
                    <th className="no-sort">offer acc.</th>
                    <th className="no-sort"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.unitId} className={`guide-row${unit?.unitId === u.unitId ? " selected" : ""}`}
                      onClick={() => selectUnit(u)}>
                      <td>
                        <div className="tt-unit-cell">
                          <span>{u.label || u.unitId}</span>
                          <span className="mono muted tt-small">{u.unitId} · {u.moveTypes.join(", ")}</span>
                        </div>
                      </td>
                      <td>
                        {u.moves}
                        <span className="muted tt-small"> · {u.users} user{u.users === 1 ? "" : "s"}</span>
                        {u.moves < LOW_SAMPLE && <span className="badge low-sample">low n</span>}
                      </td>
                      <td className={isWeakUnit(u) && (u.answerRate ?? 1) < 0.6 ? "tt-weak-text" : ""}>{pct(u.answerRate)}</td>
                      <td className={isWeakUnit(u) && (u.dropOffRate ?? 0) > 0.15 ? "tt-weak-text" : ""}>{pct(u.dropOffRate)}</td>
                      <td>{pct(u.deflectRate)}</td>
                      <td>{pct(u.offerAcceptance)}</td>
                      <td className="row-actions">
                        {unitIds.has(u.unitId) && (
                          <button className="btn btn-sm btn-ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              onFocusUnit(u.unitId);
                            }}>
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {unit && (
        <>
          {(unit.byVersion?.length ?? 0) > 1 && (
            <>
              <div className="panel-head">This unit by version</div>
              <div className="tt-small tt-drill-label">{unit.label || unit.unitId}</div>
              <table className="latency-table">
                <thead>
                  <tr><th>version</th><th>arm</th><th>moves</th><th>answered</th><th>drop-off</th><th>deflect</th><th>offer acc.</th></tr>
                </thead>
                <tbody>
                  {(unit.byVersion ?? []).map((r) => (
                    <tr key={`${r.version}-${r.arm}`}>
                      <td>{r.version !== null ? `v${r.version}` : "—"}</td>
                      <td>{armLabel(r)}</td>
                      <td>
                        {r.moves}
                        <span className="muted tt-small"> · {r.users} user{r.users === 1 ? "" : "s"}</span>
                        {r.moves < LOW_SAMPLE && <span className="badge low-sample">low n</span>}
                      </td>
                      <td>{pct(r.answerRate)}</td>
                      <td>{pct(r.dropOffRate)}</td>
                      <td>{pct(r.deflectRate)}</td>
                      <td>{pct(r.offerAcceptance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="panel-head">Moves using this unit · last {days} days</div>
          <div className="tt-small tt-drill-label">
            {unit.label || unit.unitId}. Click a move for its conversation's timeline; Transcript opens the call.
          </div>
          {callsError ? (
            <div className="state-box error">{callsError}</div>
          ) : calls === null ? (
            <div className="state-box">Loading moves…</div>
          ) : calls.length === 0 ? (
            <div className="state-box">No moves in this range.</div>
          ) : (
            <div className="table-scroll">
              <table className="guide-table">
                <thead>
                  <tr>
                    <th className="no-sort">when</th>
                    <th className="no-sort">version</th>
                    <th className="no-sort">turn</th>
                    <th className="no-sort" title="How closely the guide's move matched the unit">match</th>
                    <th className="no-sort" title="Was this the move the brief suggested?">suggested</th>
                    <th className="no-sort" title="Which path classified the move">classified by</th>
                    <th className="no-sort">outcome</th>
                    <th className="no-sort"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c, i) => (
                    <tr key={`${c.callId ?? c.sessionId}-${c.turnIndex}-${i}`}
                      className={`guide-row${c.callId && c.callId === callId ? " selected" : ""}`}
                      onClick={() => c.callId && showTimeline(c.callId)}
                      style={c.callId ? undefined : { cursor: "default" }}>
                      <td className="voice-cell">{formatDateTime(c.createdAt)}</td>
                      <td>{c.version !== null ? `v${c.version}` : "—"} · {c.arm ?? "control"}</td>
                      <td>{c.turnIndex}</td>
                      <td>{c.unitMatch ?? "—"}</td>
                      <td title={c.suggestedUnitId ? `Suggested: ${c.suggestedUnitId}` : "Nothing suggested"}>{followedText(c)}</td>
                      <td>{classifiedText(c)}</td>
                      <td>{c.outcome ?? <span className="muted">no reply</span>}</td>
                      <td className="row-actions">
                        {c.callId ? (
                          <>
                            <button className="btn btn-sm btn-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (c.callId) showTimeline(c.callId);
                              }}>
                              Timeline
                            </button>
                            <button className="btn btn-sm btn-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDrawerCallId(c.callId);
                              }}>
                              Transcript
                            </button>
                          </>
                        ) : (
                          <span className="muted tt-small">session {c.sessionId ?? "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {callId && (
        <>
          <div className="panel-head">Conversation timeline</div>
          <div className="tt-toolbar">
            <span className="mono tt-small">
              {timeline?.conversationId ? `conversation ${timeline.conversationId}` : callId}
              {timeline && ` · ${timeline.calls.length || 1} connection${timeline.calls.length === 1 ? "" : "s"}`}
            </span>
            <div className="spacer" />
            <button className="btn btn-sm btn-ghost" onClick={() => setDrawerCallId(callId)}>Transcript</button>
            <Link className="btn btn-sm btn-ghost" to={`/calls?call=${encodeURIComponent(callId)}`}>Open in Calls</Link>
          </div>
          {timelineError ? (
            <div className="state-box error">{timelineError}</div>
          ) : timeline === null ? (
            <div className="state-box">Loading timeline…</div>
          ) : timeline.events.length === 0 ? (
            <div className="state-box">No director moves recorded for this conversation.</div>
          ) : (
            <div className="table-scroll">
              <table className="latency-table">
                <thead>
                  <tr>
                    <th>turn</th><th>stage</th><th>move</th><th>unit</th><th>match</th><th>suggested</th>
                    <th>classified by</th><th>outcome</th><th>flags</th><th>version</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.events.map((m, i) => {
                    const prev = i > 0 ? timeline.events[i - 1].callId : undefined;
                    const conn = m.callId ? callIndex.get(m.callId) : undefined;
                    const call = conn !== undefined ? timeline.calls[conn] : undefined;
                    return (
                      <Fragment key={`${m.callId}-${m.turnIndex}-${i}`}>
                        {m.callId !== prev && (
                          <tr className="tt-timeline-call">
                            <td colSpan={10}>
                              connection {conn !== undefined ? conn + 1 : "?"}
                              {call && ` · ${call.inputMode === "text" ? "typed" : call.inputMode ?? "no user turn"}`}
                              {call?.startedAt && ` · ${formatDateTime(call.startedAt)}`}
                              {m.callId && (
                                <button className="btn btn-sm btn-ghost" onClick={() => setDrawerCallId(m.callId)}>
                                  Transcript
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                        <tr className={isUnitMove(m) ? "tt-move-hit" : ""}>
                          <td>{m.turnIndex}</td>
                          <td>{m.stage ?? "—"}</td>
                          <td>{m.moveType ?? "—"}</td>
                          <td className="mono">{m.unitId ?? "—"}</td>
                          <td>{m.unitMatch ?? "—"}</td>
                          <td title={m.suggestedUnitId ? `Suggested: ${m.suggestedUnitId}` : "Nothing suggested"}>{followedText(m)}</td>
                          <td>{classifiedText(m)}</td>
                          <td>{m.outcome ?? <span className="muted">no reply</span>}</td>
                          <td>{flagsText(m)}</td>
                          <td>
                            {m.topicId && m.topicId !== topic.id ? `${m.topicId} ` : ""}
                            {m.version !== null ? `v${m.version}` : "—"}{m.arm ? ` · ${m.arm}` : ""}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawerCallId && <CallDrawer callId={drawerCallId} onClose={() => setDrawerCallId(null)} />}
    </div>
  );
}
